import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2,
  Receipt,
  Search,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle2,
  DollarSign,
} from "lucide-react";
import { invoicesApi } from "../../services/api";

interface InvoiceRow {
  _id: string;
  invoiceNumber: string;
  clientId: { _id: string; displayName: string } | null;
  matterId: { _id: string; title: string; matterNumber?: string } | null;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
  status: string;
  issuedAt: string | null;
  dueAt: string | null;
  createdAt: string;
}

function formatCents(cents: number): string {
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return cents < 0 ? `-${formatted}` : formatted;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysOverdue(dueAt: string | null): number {
  if (!dueAt) return 0;
  const due = new Date(dueAt);
  const now = new Date();
  const diff = Math.floor((now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+";

function getAgingBucket(dueAt: string | null): AgingBucket {
  const days = daysOverdue(dueAt);
  if (days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  return "90+";
}

const agingLabels: Record<AgingBucket, string> = {
  current: "Current",
  "1-30": "1-30 Days",
  "31-60": "31-60 Days",
  "61-90": "61-90 Days",
  "90+": "90+ Days",
};

const agingColors: Record<AgingBucket, { bg: string; text: string; border: string }> = {
  current: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  "1-30": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "31-60": { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  "61-90": { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  "90+": { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
};

const AccountsReceivableTab: React.FC = () => {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      // Load issued and partial invoices (outstanding A/R)
      const [issuedRes, partialRes] = await Promise.all([
        invoicesApi.list({ status: "issued", limit: 500 }),
        invoicesApi.list({ status: "partial", limit: 500 }),
      ]);
      const issued = issuedRes.data?.data || issuedRes.data || [];
      const partial = partialRes.data?.data || partialRes.data || [];
      setInvoices([...issued, ...partial]);
    } catch (err) {
      console.error("Failed to load A/R invoices:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Filter by search
  const filtered = search
    ? invoices.filter((inv) => {
        const q = search.toLowerCase();
        const clientName =
          typeof inv.clientId === "object" && inv.clientId
            ? inv.clientId.displayName
            : "";
        const matterTitle =
          typeof inv.matterId === "object" && inv.matterId
            ? inv.matterId.title
            : "";
        return (
          clientName.toLowerCase().includes(q) ||
          matterTitle.toLowerCase().includes(q) ||
          (inv.invoiceNumber || "").toLowerCase().includes(q)
        );
      })
    : invoices;

  // Sort: most overdue first
  const sorted = [...filtered].sort((a, b) => {
    const aOverdue = daysOverdue(a.dueAt);
    const bOverdue = daysOverdue(b.dueAt);
    return bOverdue - aOverdue;
  });

  // Aging summary
  const agingBuckets: Record<AgingBucket, number> = {
    current: 0,
    "1-30": 0,
    "31-60": 0,
    "61-90": 0,
    "90+": 0,
  };
  const totalOutstanding = invoices.reduce((sum, inv) => {
    const balance = inv.balanceDueCents ?? (inv.totalCents - (inv.paidCents || 0));
    const bucket = getAgingBucket(inv.dueAt);
    agingBuckets[bucket] += balance;
    return sum + balance;
  }, 0);

  return (
    <div>
      {/* Aging Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        {/* Total Outstanding */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-slate-500" />
            <p className="text-xs text-slate-500 font-medium">Total Outstanding</p>
          </div>
          <p className="text-lg font-bold text-slate-800">
            {formatCents(totalOutstanding)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
          </p>
        </motion.div>

        {/* Aging buckets */}
        {(Object.keys(agingLabels) as AgingBucket[]).map((bucket, i) => {
          const colors = agingColors[bucket];
          return (
            <motion.div
              key={bucket}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i + 1) * 0.05 }}
              className={`rounded-xl shadow-sm border p-4 ${colors.bg} ${colors.border}`}
            >
              <p className={`text-xs font-medium ${colors.text} mb-1`}>
                {agingLabels[bucket]}
              </p>
              <p className={`text-lg font-bold ${colors.text}`}>
                {formatCents(agingBuckets[bucket])}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Search bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client, matter, or invoice number..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-lg font-medium text-slate-600">
            {search ? "No matching invoices" : "No outstanding invoices"}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {search
              ? "Try a different search term"
              : "All invoices have been paid in full"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand text-white text-left">
                  <th className="px-4 py-3 font-semibold">Invoice</th>
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Matter</th>
                  <th className="px-4 py-3 font-semibold">Issued</th>
                  <th className="px-4 py-3 font-semibold">Due</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-right">Paid</th>
                  <th className="px-4 py-3 font-semibold text-right">Balance Due</th>
                  <th className="px-4 py-3 font-semibold text-center">Aging</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((inv, i) => {
                  const balance = inv.balanceDueCents ?? (inv.totalCents - (inv.paidCents || 0));
                  const bucket = getAgingBucket(inv.dueAt);
                  const colors = agingColors[bucket];
                  const overdue = daysOverdue(inv.dueAt);
                  const clientName =
                    typeof inv.clientId === "object" && inv.clientId
                      ? inv.clientId.displayName
                      : "-";
                  const matterTitle =
                    typeof inv.matterId === "object" && inv.matterId
                      ? inv.matterId.title
                      : "-";

                  return (
                    <motion.tr
                      key={inv._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.015 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-slate-400" />
                          {inv.invoiceNumber || inv._id.slice(-6).toUpperCase()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">
                        {clientName}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate text-xs">
                        {matterTitle}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                        {formatDate(inv.issuedAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <span className={overdue > 0 ? "text-red-600 font-medium" : "text-slate-600"}>
                          {formatDate(inv.dueAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {formatCents(inv.totalCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-700">
                        {inv.paidCents > 0 ? formatCents(inv.paidCents) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">
                        {formatCents(balance)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors.bg} ${colors.text}`}
                        >
                          {bucket === "current" ? (
                            <Clock className="h-2.5 w-2.5" />
                          ) : (
                            <AlertTriangle className="h-2.5 w-2.5" />
                          )}
                          {agingLabels[bucket]}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                  <td colSpan={5} className="px-4 py-3 text-xs text-slate-600">
                    Total ({sorted.length} invoice{sorted.length !== 1 ? "s" : ""})
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">
                    {formatCents(sorted.reduce((s, inv) => s + inv.totalCents, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">
                    {formatCents(sorted.reduce((s, inv) => s + (inv.paidCents || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                    {formatCents(
                      sorted.reduce(
                        (s, inv) => s + (inv.balanceDueCents ?? (inv.totalCents - (inv.paidCents || 0))),
                        0
                      )
                    )}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsReceivableTab;
